import { useMemo } from 'react'
import {
  MessageSquare, Mic, Video, MessageCircle,
  Star, MoreHorizontal, Edit2, Check, Globe, Share2, Link2,
  Lock, EyeOff,
  type LucideIcon,
} from 'lucide-react'
import {
  Badge, Button, Card,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui'
import { PersonaTagBadges } from '@/components/PersonaTagBadges'
import { cn, formatRelativeTime } from '@/utils'
import type { Persona, PresentationMode } from '@/stores'
import { PersonaProfilePhoto } from './PersonaProfilePhoto'
import { buildPersonaCardMenuItems } from './personaCardMenu'

const MODE_META: Record<PresentationMode, { icon: LucideIcon; label: string }> = {
  chat: { icon: MessageSquare, label: 'Chat' },
  voice: { icon: Mic, label: 'Voice' },
  avatar: { icon: Video, label: 'Avatar' },
}

export interface PersonaCardProps {
  persona: Persona
  isDefault: boolean
  canEdit: boolean
  canArchive: boolean
  canDuplicate: boolean
  /** True when the current user owns this persona (gates the lock action). */
  isOwner: boolean
  /** True when a non-owner admin may break-glass unlock this (locked) persona. */
  canAdminUnlock: boolean
  isAdmin: boolean
  showVoice: boolean
  showAvatars: boolean
  showWhatsApp: boolean
  activeTags: string[]
  onToggleTag: (tag: string) => void
  onSetDefault: (persona: Persona, makeDefault: boolean) => void
  onEdit: (persona: Persona) => void
  onDuplicate: (persona: Persona) => void
  onArchive: (persona: Persona) => void
  onRestore: (persona: Persona) => void
  onHardDelete: (persona: Persona) => void
  onOpenSettings: (persona: Persona) => void
  onToggleLock: (persona: Persona) => void
  onAdminUnlock: (persona: Persona) => void
  /** True when this persona is in the user's hidden set (Hidden chip + Unhide action). */
  isHidden: boolean
  onToggleHidden: (persona: Persona) => void
}

export function PersonaCard(props: PersonaCardProps) {
  const {
    persona, isDefault, canEdit, canArchive, canDuplicate, isOwner, canAdminUnlock, isAdmin,
    showVoice, showAvatars, showWhatsApp,
    activeTags, onToggleTag,
    onSetDefault, onEdit, onDuplicate, onArchive, onRestore, onHardDelete,
    onOpenSettings, onToggleLock, onAdminUnlock, isHidden, onToggleHidden,
  } = props

  const isArchived = !!persona.archivedAt
  const isManaged = (persona.source ?? 'manual') !== 'manual'
  const isLocked = persona.isLocked === true
  const mode = MODE_META[persona.presentationMode]
  const ModeIcon = mode.icon

  const menuItems = useMemo(
    () => buildPersonaCardMenuItems({
      persona, isArchived, canArchive, canDuplicate, isOwner, isManaged, canAdminUnlock, isAdmin,
      isHidden, canEdit,
      onDuplicate, onArchive, onHardDelete, onToggleLock, onAdminUnlock, onToggleHidden, onOpenSettings,
    }),
    [persona, isArchived, canArchive, canDuplicate, isOwner, isManaged, canAdminUnlock, isAdmin,
     isHidden, canEdit, onDuplicate, onArchive, onHardDelete, onToggleLock, onAdminUnlock, onToggleHidden,
     onOpenSettings],
  )

  const hasStatus = isDefault || persona.isPublic || persona.isShared || persona.hasShares || isManaged || isLocked || isHidden

  return (
    <Card className={cn('relative flex flex-col gap-0 p-0 transition-shadow hover:shadow-md cursor-pointer', isDefault && 'ring-2 ring-primary')}>
      {/* Header */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <PersonaProfilePhoto name={persona.name} photoUrl={persona.avatarUrl} size="md" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-foreground" title={persona.name}>
            {persona.name}
          </h3>
          {persona.description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {persona.description}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {!isArchived && (
            <button
              type="button"
              onClick={() => onSetDefault(persona, !isDefault)}
              title={isDefault ? 'Remove as default' : 'Set as default'}
              aria-label={isDefault ? 'Remove as default' : 'Set as default'}
              aria-pressed={isDefault}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Star className={cn('h-4 w-4', isDefault && 'fill-primary text-primary')} />
            </button>
          )}
          {menuItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More actions"
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuItems.map((item) => {
                  const ItemIcon = item.icon
                  return (
                    <DropdownMenuItem
                      key={item.key}
                      onSelect={() => item.onSelect()}
                      className={cn(item.danger && 'text-destructive focus:text-destructive')}
                    >
                      <ItemIcon className="h-4 w-4" />
                      {item.label}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Capability + tags */}
      <div className="flex flex-wrap gap-1.5 px-4 pb-3">
        <Badge variant="secondary" className="flex items-center gap-1">
          <ModeIcon className="h-3 w-3" />
          {mode.label}
        </Badge>
        {showVoice && persona.voiceConfig && (
          <Badge variant="secondary">{persona.voiceConfig.provider}</Badge>
        )}
        {showAvatars && persona.avatarConfig && (
          <Badge variant="secondary">{persona.avatarConfig.provider}</Badge>
        )}
        {showWhatsApp && persona.whatsappConfig?.enabled && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            WhatsApp
          </Badge>
        )}
        <PersonaTagBadges tags={persona.tags} activeTags={activeTags} onToggle={onToggleTag} />
      </div>

      {/* Status line */}
      {hasStatus && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-3 text-xs text-muted-foreground">
          {isDefault && (
            <span className="inline-flex items-center gap-1 text-primary">
              <Check className="h-3 w-3" />Default
            </span>
          )}
          {persona.isPublic && (
            <span className="inline-flex items-center gap-1" title="Visible to all users">
              <Globe className="h-3 w-3" />Public
            </span>
          )}
          {(persona.isShared || persona.hasShares) && (
            <span className="inline-flex items-center gap-1">
              <Share2 className="h-3 w-3" />Shared
            </span>
          )}
          {isManaged && (
            <span
              className="inline-flex items-center gap-1"
              title={`Managed by ${persona.source} (external_id: ${persona.externalId ?? '—'})`}
            >
              <Link2 className="h-3 w-3" />Managed
            </span>
          )}
          {isLocked && (
            <span
              className="inline-flex items-center gap-1 text-warning-strong"
              title="Locked — only the owner can edit this persona"
            >
              <Lock className="h-3 w-3" />Locked
            </span>
          )}
          {isHidden && (
            <span
              className="inline-flex items-center gap-1"
              title="Hidden from your persona list — use Unhide to restore"
            >
              <EyeOff className="h-3 w-3" />Hidden
            </span>
          )}
        </div>
      )}

      {/* Footer — Edit is the single primary entry point (→ Workshop or the
          Settings modal, per the page); Settings… lives in the overflow menu. */}
      <div className="mt-auto border-t border-border px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {isArchived
              ? `Archived ${new Date(persona.archivedAt as number).toLocaleDateString()}`
              : `Updated ${formatRelativeTime(persona.updatedAt)}`}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {isArchived
              ? canArchive && (
                  <Button size="sm" variant="secondary" onClick={() => onRestore(persona)}>
                    Restore
                  </Button>
                )
              : (
                canEdit && (
                  <Button size="sm" variant="secondary" leftIcon={<Edit2 className="h-3.5 w-3.5" />} onClick={() => onEdit(persona)}>
                    Edit
                  </Button>
                )
              )}
          </div>
        </div>
      </div>
    </Card>
  )
}
